import useSWR from "swr";
import { MyInvoiceApi } from "../../../../apis";
import type { InvoiceSummary, PlatformInvoice, PlatformInvoiceStatus } from "../../../../apis/platformInvoice";

export interface InvoiceFilters {
  year: number;
  status: PlatformInvoiceStatus | "";
  search?: string;
}

interface RawResult {
  rows?: PlatformInvoice[];
  total?: number;
  summary?: InvoiceSummary;
}

/**
 * Read-only SWR for the caller tenant's platform invoices.
 *
 * Backend (`GET /bil/my/invoices`) supports `status`, `year`, `search`,
 * `fromDate`/`toDate`, `page`, `limit` and returns
 * `{ rows, total, page, limit, summary }`. The server `summary{}` block is the
 * source of truth for the KPIs; callers fall back to client-derived figures
 * only when it is absent (older backend build).
 */
export function useMyInvoices(filters: InvoiceFilters) {
  const { year, status, search } = filters;
  const trimmedSearch = (search ?? "").trim();

  const { data, error, isLoading, mutate } = useSWR(
    `swr.my.invoices.${year}.${status}.${trimmedSearch}`,
    () =>
      MyInvoiceApi.listMy({
        status: status || undefined,
        year,
        search: trimmedSearch || undefined,
        page: 1,
        limit: 200
      })
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any as RawResult | PlatformInvoice[] | undefined;
  const rows: PlatformInvoice[] = Array.isArray(raw) ? raw : raw?.rows ?? [];
  const summary: InvoiceSummary | undefined = Array.isArray(raw) ? undefined : raw?.summary;

  return {
    rows,
    summary,
    error,
    isLoading,
    reload: () => mutate()
  };
}
